import React, { useState, useCallback, useEffect, useRef } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import FormCard from "../FormComponents/FormCard";
import FormGroup from "../FormComponents/FormGroup";
import FormGrid from "../FormComponents/FormGrid";
import AlertBanner from "../FormComponents/AlertBanner";
import ExhibitorInfoBar from "../FormComponents/ExhibitorInfoBar";
import SignatureSection from "../FormComponents/SignatureSection";
import Icon from "../Icons/Icon";

/**
 * Step 5: Merchandising Program Details
 *
 * This step collects information about the merchandising program including:
 * - Program terms (payment, minimum orders, freight)
 * - Early order discounts and allowances
 * - New item promotions (N5%)
 * - Return authorization
 * - Vendor growth rebate program
 * - Administrative allowances
 * - Catalog participation
 */
const Step5 = ({
  exhibitorData,
  initialContract,
  onSave,
  onDataChange,
  disabled = false,
  errors = {},
  isAdmin = false,
}) => {
  const [formData, setFormData] = useState({
    regularTerms: initialContract?.regularTerms || "",
    minimumOrder: initialContract?.minimumOrder || "",
    freightTerms: initialContract?.freightTerms || "",
    standardTermsChanges: initialContract?.standardTermsChanges || "",
    earlyOrderTerms: initialContract?.earlyOrderTerms || "",
    earlyOrderTermsComments: initialContract?.earlyOrderTermsComments || "",
    earlyOrderDiscount: initialContract?.earlyOrderDiscount || "",
    earlyOrderDiscountFrom: initialContract?.earlyOrderDiscountFrom || "",
    earlyOrderDiscountTo: initialContract?.earlyOrderDiscountTo || "",
    earlyOrderDiscountComments: initialContract?.earlyOrderDiscountComments || "",
    showAllowance: initialContract?.showAllowance || "",
    showAllowanceFrom: initialContract?.showAllowanceFrom || "",
    showAllowanceTo: initialContract?.showAllowanceTo || "",
    showAllowanceComments: initialContract?.showAllowanceComments || "",
    truckLoadAllowance: initialContract?.truckLoadAllowance || "",
    truckLoadAllowanceComments: initialContract?.truckLoadAllowanceComments || "",
    special1Allowance: initialContract?.special1Allowance || "",
    special1AllowanceFrom: initialContract?.special1AllowanceFrom || "",
    special1AllowanceTo: initialContract?.special1AllowanceTo || "",
    special1AllowanceComments: initialContract?.special1AllowanceComments || "",
    special2Allowance: initialContract?.special2Allowance || "",
    special2AllowanceFrom: initialContract?.special2AllowanceFrom || "",
    special2AllowanceTo: initialContract?.special2AllowanceTo || "",
    special2AllowanceComments: initialContract?.special2AllowanceComments || "",
    dropshipBackorder: initialContract?.dropshipBackorder || "",
    prepaidMinimum: initialContract?.prepaidMinimum || "",
    distributorCoOpDFI: initialContract?.distributorCoOpDFI ?? false,
    distributorCoOpValue: initialContract?.distributorCoOpValue || "",
    distributorCoOpPercent: initialContract?.distributorCoOpPercent || "",
    n5PromotionParticipate: initialContract?.n5PromotionParticipate || "",
    n5Promotion: initialContract?.n5Promotion || "",
    returnAuthorizationParticipate: initialContract?.returnAuthorizationParticipate || "",
    returnAuthorization: initialContract?.returnAuthorization || "",
    vendorGrowthRebateParticipate: initialContract?.vendorGrowthRebateParticipate || "",
    tier1Checked: initialContract?.tier1Checked ?? false,
    tier1Sales: initialContract?.tier1Sales || "",
    tier1Rebate: initialContract?.tier1Rebate || "",
    tier2Checked: initialContract?.tier2Checked ?? false,
    tier2Sales: initialContract?.tier2Sales || "",
    tier2Rebate: initialContract?.tier2Rebate || "",
    tier3Checked: initialContract?.tier3Checked ?? false,
    tier3Sales: initialContract?.tier3Sales || "",
    tier3Rebate: initialContract?.tier3Rebate || "",
    tier4Checked: initialContract?.tier4Checked ?? false,
    tier4Sales: initialContract?.tier4Sales || "",
    tier4Rebate: initialContract?.tier4Rebate || "",
    rebateComments: initialContract?.rebateComments || "",
    adminAllowanceParticipate: initialContract?.adminAllowanceParticipate || "",
    adminAllowance: initialContract?.adminAllowance || "",
    signatureName: initialContract?.signatureName || "",
    signatureDate: initialContract?.signatureDate || "",
    signatureAcknowledge: initialContract?.signatureAcknowledge ?? false,
  });

  const formDataRef = useRef(formData);
  const autoSaveTimerRef = useRef(null);
  const hasChangedRef = useRef(false);

  // Keep ref in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange) onDataChange(formData);
  }, [formData, onDataChange]);

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChangedRef.current || disabled) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      if (onSave) onSave(formDataRef.current);
    }, 3000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [formData, onSave, disabled]);

  // Save on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (hasChangedRef.current && onSave) {
        onSave(formDataRef.current);
      }
    };
  }, [onSave]);

  const updateField = useCallback((field, value) => {
    hasChangedRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <fieldset disabled={disabled} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-page">
        <div className="form-page-header">
          <h2 className="form-page-title">Merchandising Program Details</h2>
        </div>

        {hasErrors && (
          <AlertBanner type="warning">
            <strong>Please fix the following errors:</strong>{" "}
            {Object.values(errors).join(", ")}
          </AlertBanner>
        )}

        <AlertBanner type="info">
          INSTRUCTIONS: PLEASE FILL OUT ALL FIELDS WHERE NOT PREPOPULATED – ENTER
          N/A WHERE NOT APPLICABLE. Any changes to program terms and/or details need
          to be discussed and approved by Arett Merchant
        </AlertBanner>

        <ExhibitorInfoBar exhibitorData={exhibitorData} />

        <FormCard title="Standard Terms" icon={<Icon name="tag" size={14} />}>
          <FormGrid cols={3}>
            <FormGroup label="Regular Terms" error={errors.regularTerms}>
              <input
                className={`form-input ${errors.regularTerms ? "input-error" : ""}`}
                value={formData.regularTerms}
                readOnly={!isAdmin}
                onChange={(e) => updateField("regularTerms", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Minimum Order Requirements" error={errors.minimumOrder}>
              <input
                className={`form-input ${errors.minimumOrder ? "input-error" : ""}`}
                value={formData.minimumOrder}
                readOnly={!isAdmin}
                onChange={(e) => updateField("minimumOrder", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Freight Terms" error={errors.freightTerms}>
              <input
                className={`form-input ${errors.freightTerms ? "input-error" : ""}`}
                value={formData.freightTerms}
                readOnly={!isAdmin}
                onChange={(e) => updateField("freightTerms", e.target.value)}
              />
            </FormGroup>
          </FormGrid>
          <div className="section-divider" />
          <FormGroup label="Enter any requested changes to Regular Terms, MOQ, or Freight Terms in the box below for Merchant review.">
            <textarea
              className="form-textarea"
              rows={4}
              value={formData.standardTermsChanges}
              onChange={(e) => updateField("standardTermsChanges", e.target.value)}
            />
          </FormGroup>
        </FormCard>

        <FormCard
          title="Early Order & Allowances"
          icon={<Icon name="tag" size={14} />}
        >
          {/* Early Order Terms */}
          <div
            style={{
              borderBottom: "1px solid var(--neutral-100)",
              paddingBottom: 14,
              marginBottom: 14,
            }}
          >
            <div className="flex-row" style={{ alignItems: "flex-start", gap: 12 }}>
              <label className="form-label" style={{ minWidth: 200 }}>
                Early Order Terms
              </label>
              <input
                className={`form-input${errors.earlyOrderTerms ? " input-error" : ""}`}
                value={formData.earlyOrderTerms}
                onChange={(e) => updateField("earlyOrderTerms", e.target.value)}
                style={{ width: 160 }}
              />
            </div>
            {errors.earlyOrderTerms && (
              <span className="field-error" style={{ display: "block", marginTop: 4, paddingLeft: 212 }}>
                {errors.earlyOrderTerms}
              </span>
            )}
            <div className="flex-row" style={{ marginTop: 8, paddingLeft: 212 }}>
              <span className="inline-label">Comments:</span>
              <input
                className="form-input"
                value={formData.earlyOrderTermsComments}
                onChange={(e) => updateField("earlyOrderTermsComments", e.target.value)}
                style={{ flex: 1, maxWidth: 300 }}
              />
            </div>
          </div>

          {/* Early Order Discount % */}
          <div
            style={{
              borderBottom: "1px solid var(--neutral-100)",
              paddingBottom: 14,
              marginBottom: 14,
            }}
          >
            <div className="flex-row" style={{ alignItems: "flex-start", gap: 12 }}>
              <label className="form-label" style={{ minWidth: 200 }}>
                Early Order Discount %
              </label>
              <input
                className={`form-input${errors.earlyOrderDiscount ? " input-error" : ""}`}
                value={formData.earlyOrderDiscount}
                onChange={(e) => updateField("earlyOrderDiscount", e.target.value)}
                style={{ width: 160 }}
              />
              <span className="inline-label">From:</span>
              <DatePicker
                value={formData.earlyOrderDiscountFrom ? dayjs(formData.earlyOrderDiscountFrom) : null}
                onChange={(date) => updateField("earlyOrderDiscountFrom", date ? date.format("YYYY-MM-DD") : "")}
                className="form-datepicker"
                format="MM/DD/YYYY"
                style={{ width: 140 }}
              />
              <span className="inline-label">To:</span>
              <DatePicker
                value={formData.earlyOrderDiscountTo ? dayjs(formData.earlyOrderDiscountTo) : null}
                onChange={(date) => updateField("earlyOrderDiscountTo", date ? date.format("YYYY-MM-DD") : "")}
                className="form-datepicker"
                format="MM/DD/YYYY"
                style={{ width: 140 }}
              />
            </div>
            {errors.earlyOrderDiscount && (
              <span className="field-error" style={{ display: "block", marginTop: 4, paddingLeft: 212 }}>
                {errors.earlyOrderDiscount}
              </span>
            )}
            <div className="flex-row" style={{ marginTop: 8, paddingLeft: 212 }}>
              <span className="inline-label">Comments:</span>
              <input
                className="form-input"
                value={formData.earlyOrderDiscountComments}
                onChange={(e) => updateField("earlyOrderDiscountComments", e.target.value)}
                style={{ flex: 1, maxWidth: 300 }}
              />
            </div>
          </div>

          {/* Show Allowance */}
          <div
            style={{
              borderBottom: "1px solid var(--neutral-100)",
              paddingBottom: 14,
              marginBottom: 14,
            }}
          >
            <div className="flex-row" style={{ alignItems: "flex-start", gap: 12 }}>
              <label className="form-label" style={{ minWidth: 200 }}>
                Show Allowance
              </label>
              <input
                className="form-input"
                value={formData.showAllowance}
                onChange={(e) => updateField("showAllowance", e.target.value)}
                style={{ width: 160 }}
              />
              <span className="inline-label">From:</span>
              <DatePicker
                value={formData.showAllowanceFrom ? dayjs(formData.showAllowanceFrom) : null}
                onChange={(date) => updateField("showAllowanceFrom", date ? date.format("YYYY-MM-DD") : "")}
                className="form-datepicker"
                format="MM/DD/YYYY"
                style={{ width: 140 }}
              />
              <span className="inline-label">To:</span>
              <DatePicker
                value={formData.showAllowanceTo ? dayjs(formData.showAllowanceTo) : null}
                onChange={(date) => updateField("showAllowanceTo", date ? date.format("YYYY-MM-DD") : "")}
                className="form-datepicker"
                format="MM/DD/YYYY"
                style={{ width: 140 }}
              />
            </div>
            <div className="flex-row" style={{ marginTop: 8, paddingLeft: 212 }}>
              <span className="inline-label">Comments:</span>
              <input
                className="form-input"
                value={formData.showAllowanceComments}
                onChange={(e) => updateField("showAllowanceComments", e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          {/* Truck Load/Qty Allowance */}
          <div
            style={{
              borderBottom: "1px solid var(--neutral-100)",
              paddingBottom: 14,
              marginBottom: 14,
            }}
          >
            <div className="flex-row" style={{ alignItems: "flex-start", gap: 12 }}>
              <label className="form-label" style={{ minWidth: 200 }}>
                Truck Load/Qty Allowance
              </label>
              <input
                className="form-input"
                value={formData.truckLoadAllowance}
                onChange={(e) => updateField("truckLoadAllowance", e.target.value)}
                style={{ width: 160 }}
              />
            </div>
            <div className="flex-row" style={{ marginTop: 8, paddingLeft: 212 }}>
              <span className="inline-label">Comments:</span>
              <input
                className="form-input"
                value={formData.truckLoadAllowanceComments}
                onChange={(e) => updateField("truckLoadAllowanceComments", e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          {/* Special #1 Allowance */}
          <div
            style={{
              borderBottom: "1px solid var(--neutral-100)",
              paddingBottom: 14,
              marginBottom: 14,
            }}
          >
            <div className="flex-row" style={{ alignItems: "flex-start", gap: 12 }}>
              <label className="form-label" style={{ minWidth: 200 }}>
                Special #1 Allowance
              </label>
              <input
                className="form-input"
                value={formData.special1Allowance}
                onChange={(e) => updateField("special1Allowance", e.target.value)}
                style={{ width: 160 }}
              />
              <span className="inline-label">From:</span>
              <DatePicker
                value={formData.special1AllowanceFrom ? dayjs(formData.special1AllowanceFrom) : null}
                onChange={(date) => updateField("special1AllowanceFrom", date ? date.format("YYYY-MM-DD") : "")}
                className="form-datepicker"
                format="MM/DD/YYYY"
                style={{ width: 140 }}
              />
              <span className="inline-label">To:</span>
              <DatePicker
                value={formData.special1AllowanceTo ? dayjs(formData.special1AllowanceTo) : null}
                onChange={(date) => updateField("special1AllowanceTo", date ? date.format("YYYY-MM-DD") : "")}
                className="form-datepicker"
                format="MM/DD/YYYY"
                style={{ width: 140 }}
              />
            </div>
            <div className="flex-row" style={{ marginTop: 8, paddingLeft: 212 }}>
              <span className="inline-label">Comments:</span>
              <input
                className="form-input"
                value={formData.special1AllowanceComments}
                onChange={(e) => updateField("special1AllowanceComments", e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          {/* Special #2 Allowance */}
          <div
            style={{
              borderBottom: "1px solid var(--neutral-100)",
              paddingBottom: 14,
              marginBottom: 14,
            }}
          >
            <div className="flex-row" style={{ alignItems: "flex-start", gap: 12 }}>
              <label className="form-label" style={{ minWidth: 200 }}>
                Special #2 Allowance
              </label>
              <input
                className="form-input"
                value={formData.special2Allowance}
                onChange={(e) => updateField("special2Allowance", e.target.value)}
                style={{ width: 160 }}
              />
              <span className="inline-label">From:</span>
              <DatePicker
                value={formData.special2AllowanceFrom ? dayjs(formData.special2AllowanceFrom) : null}
                onChange={(date) => updateField("special2AllowanceFrom", date ? date.format("YYYY-MM-DD") : "")}
                className="form-datepicker"
                format="MM/DD/YYYY"
                style={{ width: 140 }}
              />
              <span className="inline-label">To:</span>
              <DatePicker
                value={formData.special2AllowanceTo ? dayjs(formData.special2AllowanceTo) : null}
                onChange={(date) => updateField("special2AllowanceTo", date ? date.format("YYYY-MM-DD") : "")}
                className="form-datepicker"
                format="MM/DD/YYYY"
                style={{ width: 140 }}
              />
            </div>
            <div className="flex-row" style={{ marginTop: 8, paddingLeft: 212 }}>
              <span className="inline-label">Comments:</span>
              <input
                className="form-input"
                value={formData.special2AllowanceComments}
                onChange={(e) => updateField("special2AllowanceComments", e.target.value)}
                style={{ flex: 1 }}
              />
            </div>
          </div>

          <FormGrid cols={3} style={{ marginTop: 8 }}>
            <FormGroup label="Dropship Backorder Policy (if applicable)" error={errors.dropshipBackorder}>
              <div className={`radio-group${errors.dropshipBackorder ? " input-error" : ""}`}>
                <label className="custom-radio">
                  <input
                    type="radio"
                    name="dropship"
                    value="yes"
                    checked={formData.dropshipBackorder === "yes"}
                    onChange={(e) => updateField("dropshipBackorder", e.target.value)}
                  />
                  <span>Yes</span>
                </label>
                <label className="custom-radio">
                  <input
                    type="radio"
                    name="dropship"
                    value="no"
                    checked={formData.dropshipBackorder === "no"}
                    onChange={(e) => updateField("dropshipBackorder", e.target.value)}
                  />
                  <span>No</span>
                </label>
              </div>
            </FormGroup>
            <FormGroup label="Prepaid Minimum">
              <input
                className="form-input"
                value={formData.prepaidMinimum}
                onChange={(e) => updateField("prepaidMinimum", e.target.value)}
              />
            </FormGroup>
            <FormGroup label="Distributor Co-Op">
              <div className="flex-row">
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.distributorCoOpDFI}
                    onChange={(e) => updateField("distributorCoOpDFI", e.target.checked)}
                  />
                  <span>DFI</span>
                </label>
                <input
                  className="form-input"
                  value={formData.distributorCoOpValue}
                  onChange={(e) => updateField("distributorCoOpValue", e.target.value)}
                  style={{ width: 80 }}
                />
                <span className="inline-label">% of</span>
                <input
                  className="form-input"
                  value={formData.distributorCoOpPercent}
                  onChange={(e) => updateField("distributorCoOpPercent", e.target.value)}
                  style={{ flex: 1 }}
                />
              </div>
            </FormGroup>
          </FormGrid>
        </FormCard>

        <FormCard
          title="Featured New Item: Arett's N5% Promotion"
          icon={<Icon name="star" size={14} />}
        >
          <p className="text-small mb-12">
            Manufacturer has the opportunity to provide an additional 5% discount on
            new items that will be passed along to customers, via Line Discount,
            within the Vendor program. Items within the N5% will be featured within
            the Show Specials section and are featured with N5% icon and filter on
            arett.com Virtual Trade Show.
          </p>
          <div className="radio-group" style={{ marginBottom: 14 }}>
            <label className="custom-radio">
              <input type="radio" name="n5PromotionParticipate" value="yes"
                checked={formData.n5PromotionParticipate === "yes"}
                onChange={(e) => updateField("n5PromotionParticipate", e.target.value)} />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input type="radio" name="n5PromotionParticipate" value="no"
                checked={formData.n5PromotionParticipate === "no"}
                onChange={(e) => updateField("n5PromotionParticipate", e.target.value)} />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.n5PromotionParticipate && <span className="field-error">{errors.n5PromotionParticipate}</span>}
          {formData.n5PromotionParticipate === "yes" && (
            <>
              <textarea
                className={`form-textarea${errors.n5Promotion ? " input-error" : ""}`}
                value={formData.n5Promotion}
                onChange={(e) => updateField("n5Promotion", e.target.value)}
                rows={3}
              />
              {errors.n5Promotion && <span className="field-error">{errors.n5Promotion}</span>}
            </>
          )}
        </FormCard>

        <FormCard title="Return Authorization" icon={<Icon name="tag" size={14} />}>
          <p className="text-small mb-12">
            The undersigned agrees to issue a Return Authorization to Arett Sales
            for unsold product not stocked or not available in the following year's catalog.
            (called non-stocked items for 2026 season). Returns will be
            executed in June. All products will be returned freight prepaid and
            added to the total RGA debit amount.
          </p>
          <div className="radio-group" style={{ marginBottom: 14 }}>
            <label className="custom-radio">
              <input type="radio" name="returnAuthorizationParticipate" value="yes"
                checked={formData.returnAuthorizationParticipate === "yes"}
                onChange={(e) => updateField("returnAuthorizationParticipate", e.target.value)} />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input type="radio" name="returnAuthorizationParticipate" value="no"
                checked={formData.returnAuthorizationParticipate === "no"}
                onChange={(e) => updateField("returnAuthorizationParticipate", e.target.value)} />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.returnAuthorizationParticipate && <span className="field-error">{errors.returnAuthorizationParticipate}</span>}
          {formData.returnAuthorizationParticipate === "yes" && (
            <>
              <textarea
                className={`form-textarea${errors.returnAuthorization ? " input-error" : ""}`}
                value={formData.returnAuthorization}
                onChange={(e) => updateField("returnAuthorization", e.target.value)}
                rows={3}
              />
              {errors.returnAuthorization && <span className="field-error">{errors.returnAuthorization}</span>}
            </>
          )}
        </FormCard>

        <FormCard
          title="Vendor Growth Rebate Program"
          icon={<Icon name="award" size={14} />}
        >
          <p className="text-small mb-12">
            Annual Growth Rebate based on total Arett purchases during each fiscal year (October 1 – September 30). Upon reaching a qualifying purchase threshold, the applicable rebate applies to all current fiscal year purchases.  Effective back to dollar 1. 
          </p>
          <div className="radio-group" style={{ marginBottom: 14 }}>
            <label className="custom-radio">
              <input type="radio" name="vendorGrowthRebateParticipate" value="yes"
                checked={formData.vendorGrowthRebateParticipate === "yes"}
                onChange={(e) => updateField("vendorGrowthRebateParticipate", e.target.value)} />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input type="radio" name="vendorGrowthRebateParticipate" value="no"
                checked={formData.vendorGrowthRebateParticipate === "no"}
                onChange={(e) => updateField("vendorGrowthRebateParticipate", e.target.value)} />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.vendorGrowthRebateParticipate && <span className="field-error">{errors.vendorGrowthRebateParticipate}</span>}
          {formData.vendorGrowthRebateParticipate === "yes" && (<>
          <table className="rebate-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Tier</th>
                <th>Sales $</th>
                <th>Rebate %</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    type="checkbox"
                    checked={formData.tier1Checked}
                    onChange={(e) => updateField("tier1Checked", e.target.checked)}
                  />
                </td>
                <td>Tier 1</td>
                <td>
                  <input
                    className="form-input"
                    value={formData.tier1Sales}
                    onChange={(e) => updateField("tier1Sales", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="form-input"
                    value={formData.tier1Rebate}
                    onChange={(e) => updateField("tier1Rebate", e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <input
                    type="checkbox"
                    checked={formData.tier2Checked}
                    onChange={(e) => updateField("tier2Checked", e.target.checked)}
                  />
                </td>
                <td>Tier 2</td>
                <td>
                  <input
                    className="form-input"
                    value={formData.tier2Sales}
                    onChange={(e) => updateField("tier2Sales", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="form-input"
                    value={formData.tier2Rebate}
                    onChange={(e) => updateField("tier2Rebate", e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <input
                    type="checkbox"
                    checked={formData.tier3Checked}
                    onChange={(e) => updateField("tier3Checked", e.target.checked)}
                  />
                </td>
                <td>Tier 3</td>
                <td>
                  <input
                    className="form-input"
                    value={formData.tier3Sales}
                    onChange={(e) => updateField("tier3Sales", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="form-input"
                    value={formData.tier3Rebate}
                    onChange={(e) => updateField("tier3Rebate", e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <input
                    type="checkbox"
                    checked={formData.tier4Checked}
                    onChange={(e) => updateField("tier4Checked", e.target.checked)}
                  />
                </td>
                <td>Tier 4</td>
                <td>
                  <input
                    className="form-input"
                    value={formData.tier4Sales}
                    onChange={(e) => updateField("tier4Sales", e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="form-input"
                    value={formData.tier4Rebate}
                    onChange={(e) => updateField("tier4Rebate", e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <textarea
            className={`form-textarea mt-12${errors.rebateComments ? " input-error" : ""}`}
            value={formData.rebateComments}
            onChange={(e) => updateField("rebateComments", e.target.value)}
            rows={3}
          />
          {errors.rebateComments && <span className="field-error">{errors.rebateComments}</span>}
          </>)}
        </FormCard>

        <FormCard
          title="Administrative Allowance Chain Accounts 2%"
          icon={<Icon name="tag" size={14} />}
        >
          <p className="text-small mb-12">
            Vendor agrees to allow Arett Sales to deduct 2% allowance on a quarterly
            basis for all sales of vendor products to Chain Accounts. Chain accounts
            include any national retailer or any retailer with more than 20 Ship To
            locations. To request specific retailers from being included, please
            list below.
          </p>
          <div className="radio-group" style={{ marginBottom: 14 }}>
            <label className="custom-radio">
              <input type="radio" name="adminAllowanceParticipate" value="yes"
                checked={formData.adminAllowanceParticipate === "yes"}
                onChange={(e) => updateField("adminAllowanceParticipate", e.target.value)} />
              <span>Yes, I opt to participate</span>
            </label>
            <label className="custom-radio">
              <input type="radio" name="adminAllowanceParticipate" value="no"
                checked={formData.adminAllowanceParticipate === "no"}
                onChange={(e) => updateField("adminAllowanceParticipate", e.target.value)} />
              <span>No, I opt NOT to participate</span>
            </label>
          </div>
          {errors.adminAllowanceParticipate && <span className="field-error">{errors.adminAllowanceParticipate}</span>}
          {formData.adminAllowanceParticipate === "yes" && (
            <>
              <textarea
                className={`form-textarea${errors.adminAllowance ? " input-error" : ""}`}
                value={formData.adminAllowance}
                onChange={(e) => updateField("adminAllowance", e.target.value)}
                rows={3}
              />
              {errors.adminAllowance && <span className="field-error">{errors.adminAllowance}</span>}
            </>
          )}
        </FormCard>

        <FormCard title="Arett Catalog" icon={<Icon name="tag" size={14} />}>
          <p style={{ fontSize: 14, color: "var(--neutral-700)", lineHeight: 1.7 }}>
            Arett distributes a printed and digital catalog to all customers. Best
            performing items, as selected by the Arett Merchandising team, will be
            charged a $65 per item fee to be included in the catalog.
          </p>
        </FormCard>

        <FormCard>
          <AlertBanner type="info">
            <strong>Program Changes Penalty Fee:</strong> There will be a $500
            charge for any change made to your program after it has been submitted.
            Electronic signature indicates I have read and accept these details.
          </AlertBanner>
          <div className="section-divider" />
          <SignatureSection
            legal={false}
            name={formData.signatureName}
            date={formData.signatureDate}
            acknowledge={formData.signatureAcknowledge}
            onNameChange={(name) => updateField("signatureName", name)}
            onDateChange={(date) => updateField("signatureDate", date)}
            onAcknowledge={(v) => updateField("signatureAcknowledge", v)}
          />
          {(errors.signatureName || errors.signatureDate) && (
            <div className="step-error-summary" style={{ marginTop: 8 }}>
              {errors.signatureName && <span className="field-error">{errors.signatureName}</span>}
              {errors.signatureDate && <span className="field-error">{errors.signatureDate}</span>}
            </div>
          )}
        </FormCard>
      </div>
    </fieldset>
  );
};

export default Step5;
